import { Button, Card, Form, Input, Select, Space, Statistic, Switch, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import { formatMoney } from '@/utils/format';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type CollectionRow = {
  student_id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  demand_count: number;
  outstanding_amount: number;
  max_age_days: number;
  age_bucket: string;
  uninvoiced_count: number;
  on_hold_count: number;
  broken_promise_count: number;
  overdue_promise_count: number;
  last_chase_at?: string | null;
};

type CollectionPayload = {
  rows: CollectionRow[];
  summary: {
    period_key: string | null;
    aging: Record<string, { student_count: number; outstanding_amount: number }>;
    on_hold_students: number;
    broken_promises: number;
    overdue_promises: number;
    row_count: number;
  };
};

const AGE_OPTIONS = [
  { value: '', label: 'All ages' },
  { value: '0-30', label: '0–30 days' },
  { value: '31-60', label: '31–60 days' },
  { value: '61-90', label: '61–90 days' },
  { value: '90+', label: '90+ days' },
];

export default function FeeCollectionWorkbenchPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canChase = Boolean(
    user?.permissions?.includes('education.fee.collect') ||
      user?.permissions?.includes('education.fee.manage') ||
      user?.permissions?.includes('education.admin')
  );
  const canSweep = Boolean(
    canChase ||
      user?.permissions?.includes('education.fee.promise')
  );

  const [periodKey, setPeriodKey] = useState('');
  const [q, setQ] = useState('');
  const [ageBucket, setAgeBucket] = useState('');
  const [hasDisputeHold, setHasDisputeHold] = useState(false);
  const [hasBrokenPromise, setHasBrokenPromise] = useState(false);
  const [hasOverduePromise, setHasOverduePromise] = useState(false);

  const board = useQuery({
    queryKey: [
      'education-fee-collection',
      periodKey,
      q,
      ageBucket,
      hasDisputeHold,
      hasBrokenPromise,
      hasOverduePromise,
    ],
    queryFn: () =>
      api.get<CollectionPayload>('/education/fees/collection', {
        params: {
          period_key: periodKey || undefined,
          q: q || undefined,
          age_bucket: ageBucket || undefined,
          has_dispute_hold: hasDisputeHold ? '1' : undefined,
          has_broken_promise: hasBrokenPromise ? '1' : undefined,
          has_overdue_promise: hasOverduePromise ? '1' : undefined,
        },
      }),
  });

  const data = board.data?.data;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['education-fee-collection'] });

  const chase = useMutation({
    mutationFn: (studentId: string) =>
      api.post('/education/fees/collection/chase', {
        student_id: studentId,
        channel: 'both',
      }),
    onSuccess: (result) => {
      const body = result.data as {
        status?: string;
        notified_user_count?: number;
        notified_email_count?: number;
      };
      message.success(
        t('feeChaseDone', {
          status: body?.status ?? 'sent',
          users: body?.notified_user_count ?? 0,
          emails: body?.notified_email_count ?? 0,
        })
      );
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const sweep = useMutation({
    mutationFn: () => api.post('/education/fees/collection/sweep-broken-promises', {}),
    onSuccess: (result) => {
      const body = result.data as { updated?: number };
      message.success(t('feeSweepBrokenDone', { count: body?.updated ?? 0 }));
      refresh();
      queryClient.invalidateQueries({ queryKey: ['education-fee-promises'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('feeCollection')}
      description={t('feeCollectionIntro')}
      extra={
        <Space wrap>
          {canSweep ? (
            <Button loading={sweep.isPending} onClick={() => sweep.mutate()}>
              {t('sweepBrokenPromises')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/fees/outstanding')}>{t('feeOutstanding')}</Button>
          <Button onClick={() => navigate('/education/fees/promises')}>{t('feePromises')}</Button>
          <Button onClick={() => navigate('/education/fees')}>{t('backToFees')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => board.refetch()}>
            <Form.Item label={t('periodKey')}>
              <Input
                value={periodKey}
                onChange={(e) => setPeriodKey(e.target.value)}
                placeholder="2026-Q1"
                allowClear
                style={{ width: 140 }}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('feeOutstandingSearchHint')}
                allowClear
                style={{ width: 200 }}
              />
            </Form.Item>
            <Form.Item label={t('ageBucket')}>
              <Select
                value={ageBucket}
                onChange={setAgeBucket}
                options={AGE_OPTIONS}
                style={{ width: 140 }}
              />
            </Form.Item>
            <Form.Item label={t('disputeHoldFilter')}>
              <Switch checked={hasDisputeHold} onChange={setHasDisputeHold} />
            </Form.Item>
            <Form.Item label={t('brokenPromiseFilter')}>
              <Switch checked={hasBrokenPromise} onChange={setHasBrokenPromise} />
            </Form.Item>
            <Form.Item label={t('overduePromiseFilter')}>
              <Switch checked={hasOverduePromise} onChange={setHasOverduePromise} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={board.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>

        <Card size="small" loading={board.isLoading}>
          <Space size="large" wrap>
            <Statistic title={t('onHoldStudents')} value={data?.summary?.on_hold_students ?? 0} />
            <Statistic title={t('brokenPromises')} value={data?.summary?.broken_promises ?? 0} />
            <Statistic title={t('overduePromises')} value={data?.summary?.overdue_promises ?? 0} />
            {(['0-30', '31-60', '61-90', '90+'] as const).map((bucket) => (
              <Statistic
                key={bucket}
                title={`${t('ageBucket')} ${bucket}`}
                value={data?.summary?.aging?.[bucket]?.outstanding_amount ?? 0}
                precision={2}
                prefix="₹"
              />
            ))}
          </Space>
        </Card>

        <Card size="small">
          <Table
            rowKey={(row) => row.student_id}
            size="small"
            loading={board.isLoading}
            dataSource={data?.rows ?? []}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: t('student'),
                render: (_: unknown, row: CollectionRow) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
              },
              {
                title: t('outstanding'),
                dataIndex: 'outstanding_amount',
                width: 120,
                render: (v: number) =>
                  formatMoney(Number(v), { currency: 'INR', display: 'plain', locale: 'en-IN' }),
              },
              { title: t('ageDays'), dataIndex: 'max_age_days', width: 90 },
              {
                title: t('ageBucket'),
                dataIndex: 'age_bucket',
                width: 90,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('collectionHold'),
                width: 100,
                render: (_: unknown, row: CollectionRow) =>
                  Number(row.on_hold_count) > 0 ? (
                    <Tag color="orange">{t('onHold')}</Tag>
                  ) : (
                    '—'
                  ),
              },
              {
                title: t('promises'),
                width: 140,
                render: (_: unknown, row: CollectionRow) => (
                  <Space size={4}>
                    {Number(row.broken_promise_count) > 0 ? (
                      <Tag color="red">{t('brokenShort', { count: row.broken_promise_count })}</Tag>
                    ) : null}
                    {Number(row.overdue_promise_count) > 0 ? (
                      <Tag color="volcano">{t('overdueShort', { count: row.overdue_promise_count })}</Tag>
                    ) : null}
                    {!row.broken_promise_count && !row.overdue_promise_count ? '—' : null}
                  </Space>
                ),
              },
              {
                title: t('lastChase'),
                dataIndex: 'last_chase_at',
                width: 160,
                render: (v: string | null | undefined) =>
                  v ? String(v).replace('T', ' ').slice(0, 16) : '—',
              },
              {
                title: t('actions'),
                width: 280,
                render: (_: unknown, row: CollectionRow) => (
                  <Space size="small" wrap>
                    <Button
                      size="small"
                      onClick={() =>
                        navigate(`/education/fees/family-statement?student_id=${row.student_id}`)
                      }
                    >
                      {t('feeFamilyStatement')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => navigate(`/education/fees/students/${row.student_id}`)}
                    >
                      {t('ledger')}
                    </Button>
                    {canChase ? (
                      <Button
                        size="small"
                        type="primary"
                        loading={chase.isPending}
                        disabled={Number(row.on_hold_count) > 0}
                        onClick={() => chase.mutate(row.student_id)}
                      >
                        {t('chaseGuardian')}
                      </Button>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
